import { API_BASE_URL } from '../config/constants';

/**
 * Service pour le statut premium et l'activation de l'essai gratuit.
 */
async function getPremiumStatus() {
  const response = await fetch(`${API_BASE_URL}/premium/status`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) return null;
  const data = await response.json();
  return data.success ? data : null;
}

/**
 * Demande l'envoi d'un email de confirmation pour activer la période d'essai.
 * L'utilisateur doit cliquer sur le lien dans l'email pour activer.
 * Requiert une adresse email dans le profil.
 * @returns {Promise<{ success: boolean, message?: string, error?: string }>}
 */
async function requestTrial() {
  const response = await fetch(`${API_BASE_URL}/premium/trial/request`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return { success: false, error: data.error || 'Erreur lors de la demande d\'essai' };
  }
  return { success: data.success, message: data.message, error: data.error };
}

/**
 * Active directement la période d'essai (sans email).
 * Préférer requestTrial() pour les utilisateurs avec email.
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
async function activateTrial() {
  const response = await fetch(`${API_BASE_URL}/premium/trial`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return { success: false, error: data.error || 'Erreur lors de l\'activation de l\'essai' };
  }
  return { success: data.success, error: data.error };
}

/**
 * Démarre un abonnement Stripe (redirection vers Checkout).
 * @returns {Promise<{ success: boolean, checkoutUrl?: string, error?: string }>}
 */
async function createSubscription() {
  const response = await fetch(`${API_BASE_URL}/premium/subscribe`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      success: false,
      error: data.error || 'Impossible de démarrer l\'abonnement',
    };
  }
  return {
    success: data.success,
    checkoutUrl: data.checkoutUrl,
    error: data.error,
  };
}

/**
 * Annule l'abonnement Stripe côté serveur.
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
async function cancelSubscription() {
  const response = await fetch(`${API_BASE_URL}/premium/cancel`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      success: false,
      error: data.error || 'Impossible d\'annuler l\'abonnement',
    };
  }
  return { success: data.success, error: data.error };
}

/**
 * Ouvre le Customer Portal Stripe (moyen de paiement, factures, annulation).
 * @returns {Promise<{ success: boolean, portalUrl?: string, error?: string }>}
 */
async function createBillingPortalSession() {
  const response = await fetch(`${API_BASE_URL}/premium/billing-portal`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      success: false,
      error: data.error || 'Impossible d’ouvrir la gestion de l’abonnement',
    };
  }
  return {
    success: data.success,
    portalUrl: data.portalUrl,
    error: data.error,
  };
}

export const premiumService = {
  getPremiumStatus,
  requestTrial,
  activateTrial,
  createSubscription,
  createBillingPortalSession,
  cancelSubscription,
};
